import { useEffect, useState } from "react";
import { CalendarClock, CalendarDays, CalendarPlus, Download, MessageSquarePlus, Play, Radio } from "lucide-react";
import { fetchGuestCalendar, guestIcsUrl } from "@/lib/api";
import { askGuest } from "@/components/atfq/askGuest";
import { MonthGrid } from "@/components/MonthGrid";
import { addMonths, countdown, fmtDay, fmtTime, googleCalendarUrl, viewerIsET } from "@/lib/calendar";

const useCalendar = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetchGuestCalendar().then(setData).catch(() => setData({ upcoming: [], recorded: [], next: null }));
  }, []);
  return data;
};

const useTick = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  return now;
};

const AddToCal = ({ g, compact = false }) => (
  <div className="flex flex-wrap gap-2">
    <a
      href={googleCalendarUrl(g, window.location.origin)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="atfq-cal-google"
      className={`inline-flex items-center gap-1.5 border border-slate-300 hover:border-[#0A192F] px-3 ${compact ? "py-1.5 text-xs" : "py-2 text-xs"} transition-colors`}
    >
      <CalendarPlus className="w-3.5 h-3.5" /> Google Calendar
    </a>
    <a
      href={guestIcsUrl(g.id)}
      data-testid="atfq-cal-ics"
      className={`inline-flex items-center gap-1.5 border border-slate-300 hover:border-[#0A192F] px-3 ${compact ? "py-1.5 text-xs" : "py-2 text-xs"} transition-colors`}
    >
      <Download className="w-3.5 h-3.5" /> .ics
    </a>
  </div>
);

const Rescheduled = ({ g, light = false }) =>
  g.rescheduled ? (
    <span data-testid="atfq-cal-rescheduled" title={`Originally ${g.rescheduled_from_label}`}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${light ? "border-amber-300 text-amber-200" : "border-amber-400 bg-amber-50 text-amber-800"}`}>
      <CalendarClock className="w-3 h-3" /> Rescheduled
    </span>
  ) : null;

const When = ({ g, className = "" }) => (
  <div className={`text-sm ${className}`} data-testid="atfq-cal-when">
    <span className="font-medium">{fmtDay(g.recording_at_utc)}</span> · {fmtTime(g.recording_at_utc)}
    {!viewerIsET() && <span className="text-slate-500"> · {g.recording_et_label.split(" · ")[1]}</span>}
    {g.format_label && <span className="text-slate-500"> · {g.format_label}</span>}
  </div>
);

const NextCard = ({ g, now }) => {
  const cd = countdown(g.recording_at_utc, now);
  return (
    <article data-testid="atfq-cal-next" className="bg-[#0A192F] text-white p-8 md:p-10 relative overflow-hidden">
      <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-[var(--accent)]/20 blur-3xl" />
      <div className="text-xs tracking-widest uppercase text-[var(--accent)] flex items-center gap-2">
        <Radio className={`w-3.5 h-3.5 ${cd.live ? "animate-pulse" : ""}`} /> {cd.live ? "Recording now" : "Next recording"}
      </div>
      <div className="mt-4 font-display text-5xl md:text-6xl tracking-tight leading-none" data-testid="atfq-cal-countdown">
        {cd.live ? "LIVE" : cd.label}
      </div>
      <div className="mt-6 font-display text-2xl md:text-3xl tracking-tight flex flex-wrap items-center gap-3">{g.name} <Rescheduled g={g} light /></div>
      <p className="text-sm text-white/70">{g.role}{g.affiliation ? ` · ${g.affiliation}` : ""}</p>
      <When g={g} className="mt-4 text-white/90" />
      {g.rescheduled && <p className="mt-1 text-xs text-white/60">Moved from {g.rescheduled_from_label}</p>}
      {g.public_blurb && <p className="mt-4 text-sm text-white/80 leading-relaxed max-w-md">{g.public_blurb}</p>}
      <div className="mt-6 flex flex-wrap gap-3">
        <button type="button" data-testid="atfq-cal-ask" onClick={() => askGuest(g)}
          className="inline-flex items-center gap-2 bg-[var(--accent)] hover:bg-white hover:text-[#0A192F] text-white px-5 py-2.5 text-sm font-medium transition-colors">
          <MessageSquarePlus className="w-4 h-4" /> Send {g.name.split(" ")[0]} a question
        </button>
      </div>
      <div className="mt-3 [&_a]:border-white/30 [&_a]:text-white [&_a:hover]:border-white"><AddToCal g={g} compact /></div>
    </article>
  );
};

const UpcomingRow = ({ g }) => (
  <li data-testid={`atfq-cal-item-${g.id}`} className="border-t border-slate-200 py-5 flex flex-wrap items-start justify-between gap-4">
    <div className="min-w-0">
      <div className="font-display text-xl text-[#0A192F] tracking-tight flex flex-wrap items-center gap-2">{g.name} <Rescheduled g={g} /></div>
      <p className="text-sm text-slate-600">{g.role}{g.affiliation ? ` · ${g.affiliation}` : ""}</p>
      <When g={g} className="mt-1 text-slate-700" />
      {g.rescheduled && <p className="text-xs text-amber-700">Moved from {g.rescheduled_from_label}</p>}
    </div>
    <div className="flex flex-col items-end gap-2">
      <button type="button" data-testid="atfq-cal-item-ask" onClick={() => askGuest(g)}
        className="inline-flex items-center gap-1.5 text-xs text-[var(--accent)] hover:underline">
        <MessageSquarePlus className="w-3.5 h-3.5" /> Send a question
      </button>
      <AddToCal g={g} compact />
    </div>
  </li>
);

const Recorded = ({ list }) =>
  list.length ? (
    <div data-testid="atfq-cal-recorded" className="mt-16">
      <div className="text-xs tracking-tight text-slate-500 mb-4">Recently recorded</div>
      <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {list.map((g) => (
          <li key={g.id} className="border-t border-slate-200 pt-4">
            <div className="font-display text-lg text-[#0A192F] tracking-tight">{g.name}</div>
            <p className="text-xs text-slate-500">{fmtDay(g.recording_at_utc)}</p>
            <a href={g.episode_url} target="_blank" rel="noopener noreferrer" data-testid="atfq-cal-episode-link"
              className="mt-2 inline-flex items-center gap-1.5 text-sm text-[var(--accent)] hover:underline">
              <Play className="w-3.5 h-3.5 fill-current" /> Watch the episode
            </a>
          </li>
        ))}
      </ul>
    </div>
  ) : null;

export const RecordingCalendar = ({ standalone = false }) => {
  const data = useCalendar();
  const now = useTick();
  const [month, setMonth] = useState(new Date());

  useEffect(() => {
    if (data?.next) setMonth(new Date(data.next.recording_at_utc));
  }, [data]);

  if (!data) return null;
  if (!data.upcoming.length && !data.recorded.length && !standalone) return null;

  const events = data.upcoming.map((g) => ({ ...g, at: g.recording_at_utc }));

  return (
    <div id="atfq-calendar" data-testid="atfq-calendar" className={standalone ? "" : "mt-28 md:mt-32 border-t border-slate-200 pt-16"}>
      <div className="text-xs tracking-tight text-[var(--accent)] mb-3 flex items-center gap-2">
        <CalendarDays className="w-3.5 h-3.5" /> Recording Calendar
      </div>
      <h3 className="font-display text-3xl md:text-5xl text-[#0A192F] leading-tight tracking-tight max-w-3xl">
        Who&apos;s answering next — and when.
      </h3>
      <p className="mt-4 text-sm text-slate-500">
        Times shown in your local time zone{!viewerIsET() && " (Eastern noted)"}. Send your question before we hit record.
      </p>

      {data.upcoming.length === 0 ? (
        <p data-testid="atfq-cal-empty" className="mt-10 text-sm text-slate-500 border-t border-slate-200 pt-8">
          No recordings on the books right now — the next booked guest lands here the moment it&apos;s scheduled.
        </p>
      ) : (
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <NextCard g={data.next} now={now} />
          </div>
          <div className="lg:col-span-7">
            <MonthGrid
              month={month}
              onMonth={(n) => setMonth((m) => addMonths(m, n))}
              events={events}
              testId="atfq-cal-grid"
              renderEvent={(ev) => (
                <button key={ev.id} type="button" onClick={() => askGuest(ev)} title={`${ev.name} — send a question`}
                  data-testid={`atfq-cal-grid-event-${ev.id}`}
                  className="w-full text-left bg-[var(--accent)] text-white text-[10px] md:text-xs leading-tight px-1.5 py-1 truncate hover:bg-[#0A192F] transition-colors">
                  {fmtTime(ev.at).replace(/\s[A-Z]{2,5}$/, "")} · {ev.name.split(" ").slice(-1)[0]}
                </button>
              )}
            />
            <ul className="mt-8" data-testid="atfq-cal-list">
              {data.upcoming.map((g) => <UpcomingRow key={g.id} g={g} />)}
            </ul>
          </div>
        </div>
      )}
      <Recorded list={data.recorded} />
    </div>
  );
};

export default RecordingCalendar;
